import { zodResolver } from "@hookform/resolvers/zod";
import { Action } from "@packages/shared-types";
import { useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { Ban, Loader2, Plus, Trash2 } from "lucide-react";
import { useId, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import {
	getPayableQueryOptions,
	useAddBillPayment,
	useDeleteBillPayment,
	useVoidPayable,
} from "@/api/financial";
import { PageHeader } from "@/components/PageHeader";
import {
	AlertDialog,
	AlertDialogAction,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
	Form,
	FormControl,
	FormField,
	FormItem,
	FormLabel,
	FormMessage,
	FormRootMessage,
	useGuardedSubmit,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { NumberField } from "@/components/ui/number-field";
import { ResponsiveFormDialog } from "@/components/ui/responsive-form-dialog";
import { SubmitButton } from "@/components/ui/submit-button";
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from "@/components/ui/table";
import { Textarea } from "@/components/ui/textarea";
import { useHasPermission } from "@/hooks/useAccessControl";
import { useDateFormatter } from "@/hooks/useDateFormatter";
import { toCents } from "@/utils/decimal";
import { formatMoney } from "@/utils/financial";
import { dateInput, money } from "@/utils/formSchemas";
import { PayableStatusBadge } from "./-components/PayableBadges";

export const Route = createFileRoute(
	"/_auth/app/_app/org/$orgId/financial/payables/$billId",
)({
	loader: async ({ context, params }) => {
		await context.queryClient.ensureQueryData(
			getPayableQueryOptions(params.orgId, params.billId),
		);
	},
	component: PayableDetailPage,
});

type InstallmentTarget = { id: string; outstanding: string; seq: number };

const INSTALLMENT_STATUS_CLASS: Record<string, string> = {
	OPEN: "text-primary",
	PARTIAL: "text-warning",
	SETTLED: "text-success",
	OVERDUE: "text-error",
};

function PayableDetailPage() {
	const { orgId, billId } = Route.useParams();
	const { t } = useTranslation();
	const { formatDateOnly } = useDateFormatter();
	const canManage = useHasPermission(orgId)(Action.MANAGE_FINANCE);

	const [payTarget, setPayTarget] = useState<InstallmentTarget | null>(null);
	const [deletingPaymentId, setDeletingPaymentId] = useState<string | null>(
		null,
	);
	const [isVoidOpen, setIsVoidOpen] = useState(false);

	const { data: bill } = useSuspenseQuery(
		getPayableQueryOptions(orgId, billId),
	);

	const addPaymentMutation = useAddBillPayment();
	const deletePaymentMutation = useDeleteBillPayment();
	const voidMutation = useVoidPayable();

	const currency = bill.currency;
	const isVoid = bill.isVoid;
	const canRecord = canManage && !isVoid && bill.status !== "SETTLED";

	const seqByInstallment = new Map(bill.installments.map((i) => [i.id, i.seq]));

	return (
		<div className="mx-auto flex w-full max-w-3xl flex-col gap-6">
			<PageHeader title={bill.party} showBackButton />

			{isVoid && (
				<Card className="border-error/40 bg-error/5">
					<CardContent className="flex items-center gap-3 p-4">
						<Ban className="h-5 w-5 text-error" />
						<div>
							<p className="font-medium text-error">
								{t("financial.payables.void.title")}
							</p>
							<p className="text-sm text-base-content/70">
								{t("financial.payables.void.description")}
							</p>
						</div>
					</CardContent>
				</Card>
			)}

			<Card>
				<CardContent className="flex flex-col gap-4 p-5">
					<div className="flex items-start justify-between gap-3">
						<div className="flex flex-wrap items-center gap-2">
							<PayableStatusBadge status={bill.displayStatus} />
							{bill.description && (
								<span className="text-sm text-base-content/70">
									{bill.description}
								</span>
							)}
						</div>
						<span className="text-xl font-semibold">
							{formatMoney(bill.outstandingAmount, currency)}
						</span>
					</div>

					<Row
						label={t("financial.payables.issueDate")}
						value={formatDateOnly(new Date(bill.issueDate).getTime())}
					/>
					<Row
						label={t("financial.payables.total")}
						value={formatMoney(bill.totalAmount, currency)}
					/>
					<Row
						label={t("financial.payables.paid")}
						value={formatMoney(bill.paidAmount, currency)}
					/>
					<Row
						label={t("financial.payables.outstanding")}
						value={formatMoney(bill.outstandingAmount, currency)}
					/>
					{bill.notes && (
						<Row label={t("financial.payables.notes")} value={bill.notes} />
					)}
				</CardContent>
			</Card>

			{canManage && !isVoid && (
				<div className="flex flex-wrap gap-2">
					<Button
						variant="outline"
						className="text-error"
						onClick={() => setIsVoidOpen(true)}
						disabled={voidMutation.isPending}
					>
						<Ban className="mr-2 h-4 w-4" />
						{t("financial.payables.void.action")}
					</Button>
				</div>
			)}

			{/* Installments */}
			{bill.installments.length > 0 && (
				<Card>
					<CardContent className="p-0">
						<Table>
							<TableHeader>
								<TableRow>
									<TableHead>
										{t("financial.payables.installments.columns.seq")}
									</TableHead>
									<TableHead>
										{t("financial.payables.installments.columns.dueDate")}
									</TableHead>
									<TableHead className="text-right">
										{t("financial.payables.installments.columns.amount")}
									</TableHead>
									<TableHead className="text-right hidden sm:table-cell">
										{t("financial.payables.installments.columns.outstanding")}
									</TableHead>
									<TableHead>
										{t("financial.payables.installments.columns.status")}
									</TableHead>
									{canRecord && <TableHead className="text-right" />}
								</TableRow>
							</TableHeader>
							<TableBody>
								{bill.installments.map((inst) => (
									<TableRow key={inst.id}>
										<TableCell>{inst.seq}</TableCell>
										<TableCell
											className={inst.isOverdue ? "text-error" : undefined}
										>
											{formatDateOnly(new Date(inst.dueDate).getTime())}
										</TableCell>
										<TableCell className="text-right">
											{formatMoney(inst.amount, currency)}
										</TableCell>
										<TableCell className="text-right hidden sm:table-cell font-medium">
											{formatMoney(inst.outstandingAmount, currency)}
										</TableCell>
										<TableCell
											className={
												INSTALLMENT_STATUS_CLASS[inst.displayStatus] ??
												undefined
											}
										>
											{t(
												`financial.payables.installments.statuses.${inst.displayStatus}`,
											)}
										</TableCell>
										{canRecord && (
											<TableCell className="text-right">
												{inst.displayStatus !== "SETTLED" && (
													<Button
														size="sm"
														variant="ghost"
														data-testid="bill-add-payment"
														onClick={() =>
															setPayTarget({
																id: inst.id,
																outstanding: inst.outstandingAmount,
																seq: inst.seq,
															})
														}
													>
														<Plus className="mr-1 h-3 w-3" />
														{t("financial.payables.payments.add.action")}
													</Button>
												)}
											</TableCell>
										)}
									</TableRow>
								))}
							</TableBody>
						</Table>
					</CardContent>
				</Card>
			)}

			{/* Payments */}
			<Card>
				<CardContent className="p-0">
					{bill.payments.length === 0 ? (
						<p className="py-8 text-center text-sm text-base-content/60">
							{t("financial.payables.payments.empty")}
						</p>
					) : (
						<Table>
							<TableHeader>
								<TableRow>
									<TableHead>
										{t("financial.payables.payments.columns.date")}
									</TableHead>
									<TableHead>
										{t("financial.payables.payments.columns.installment")}
									</TableHead>
									<TableHead className="text-right">
										{t("financial.payables.payments.columns.amount")}
									</TableHead>
									<TableHead className="hidden sm:table-cell">
										{t("financial.payables.payments.columns.notes")}
									</TableHead>
									{canManage && !isVoid && <TableHead className="text-right" />}
								</TableRow>
							</TableHeader>
							<TableBody>
								{bill.payments.map((payment) => (
									<TableRow key={payment.id}>
										<TableCell>
											{formatDateOnly(new Date(payment.paidDate).getTime())}
										</TableCell>
										<TableCell>
											{seqByInstallment.get(payment.installmentId) ?? "—"}
										</TableCell>
										<TableCell className="text-right font-medium text-success">
											{formatMoney(payment.amount, currency)}
										</TableCell>
										<TableCell className="hidden sm:table-cell text-base-content/70">
											{payment.notes ?? "—"}
										</TableCell>
										{canManage && !isVoid && (
											<TableCell className="text-right">
												<Button
													size="sm"
													variant="ghost"
													className="text-error"
													onClick={() => setDeletingPaymentId(payment.id)}
												>
													<Trash2 className="h-3 w-3" />
												</Button>
											</TableCell>
										)}
									</TableRow>
								))}
							</TableBody>
						</Table>
					)}
				</CardContent>
			</Card>

			{payTarget && (
				<AddPaymentDialog
					defaultAmount={payTarget.outstanding}
					isSubmitting={addPaymentMutation.isPending}
					onClose={() => setPayTarget(null)}
					onConfirm={(data) =>
						addPaymentMutation
							.mutateAsync({
								organizationId: orgId,
								billId,
								installmentId: payTarget.id,
								data,
							})
							.then(() => setPayTarget(null))
					}
				/>
			)}

			<AlertDialog
				open={deletingPaymentId !== null}
				onOpenChange={(open) => !open && setDeletingPaymentId(null)}
			>
				<AlertDialogContent>
					<AlertDialogHeader>
						<AlertDialogTitle>
							{t("financial.payables.payments.delete.title")}
						</AlertDialogTitle>
						<AlertDialogDescription>
							{t("financial.payables.payments.delete.confirm")}
						</AlertDialogDescription>
					</AlertDialogHeader>
					<AlertDialogFooter>
						<AlertDialogCancel disabled={deletePaymentMutation.isPending}>
							{t("common.cancel")}
						</AlertDialogCancel>
						<AlertDialogAction
							onClick={() => {
								if (!deletingPaymentId) return;
								return deletePaymentMutation
									.mutateAsync({
										organizationId: orgId,
										billId,
										paymentId: deletingPaymentId,
									})
									.then(() => setDeletingPaymentId(null));
							}}
							disabled={deletePaymentMutation.isPending}
						>
							{deletePaymentMutation.isPending && (
								<Loader2 className="mr-2 h-4 w-4 animate-spin" />
							)}
							{t("financial.payables.payments.delete.action")}
						</AlertDialogAction>
					</AlertDialogFooter>
				</AlertDialogContent>
			</AlertDialog>

			<AlertDialog open={isVoidOpen} onOpenChange={setIsVoidOpen}>
				<AlertDialogContent>
					<AlertDialogHeader>
						<AlertDialogTitle>
							{t("financial.payables.void.title")}
						</AlertDialogTitle>
						<AlertDialogDescription>
							{t("financial.payables.void.confirm")}
						</AlertDialogDescription>
					</AlertDialogHeader>
					<AlertDialogFooter>
						<AlertDialogCancel disabled={voidMutation.isPending}>
							{t("common.cancel")}
						</AlertDialogCancel>
						<AlertDialogAction
							onClick={() =>
								voidMutation.mutate(
									{ organizationId: orgId, billId },
									{ onSuccess: () => setIsVoidOpen(false) },
								)
							}
							disabled={voidMutation.isPending}
						>
							{voidMutation.isPending && (
								<Loader2 className="mr-2 h-4 w-4 animate-spin" />
							)}
							{t("financial.payables.void.action")}
						</AlertDialogAction>
					</AlertDialogFooter>
				</AlertDialogContent>
			</AlertDialog>
		</div>
	);
}

function Row({ label, value }: { label: string; value: string }) {
	return (
		<div className="flex items-start justify-between gap-3 border-b border-base-200 pb-2 text-sm last:border-0">
			<span className="text-base-content/60">{label}</span>
			<span className="text-right font-medium">{value}</span>
		</div>
	);
}

const addPaymentSchema = z.object({
	amount: money(),
	paidDate: dateInput(),
	notes: z.string(),
});
type AddPaymentInput = z.input<typeof addPaymentSchema>;
type AddPaymentOutput = z.output<typeof addPaymentSchema>;

function AddPaymentDialog({
	defaultAmount,
	isSubmitting,
	onConfirm,
	onClose,
}: {
	defaultAmount: string;
	isSubmitting: boolean;
	onConfirm: (data: {
		amount: string;
		paidDate: string;
		notes?: string;
	}) => Promise<unknown> | undefined;
	onClose: () => void;
}) {
	const { t } = useTranslation();
	const { todayForDateInput } = useDateFormatter();
	const formId = useId();
	const schema = addPaymentSchema.extend({
		amount: money().refine(
			(value) => (toCents(value) ?? 0) <= (toCents(defaultAmount) ?? 0),
			"forms.validation.exceedsOutstanding",
		),
	});

	const form = useForm<AddPaymentInput, unknown, AddPaymentOutput>({
		resolver: zodResolver(schema),
		mode: "onTouched",
		reValidateMode: "onChange",
		defaultValues: {
			amount: defaultAmount,
			paidDate: todayForDateInput(),
			notes: "",
		},
	});

	const guardedSubmit = useGuardedSubmit(form, (values) =>
		onConfirm({
			amount: values.amount,
			paidDate: new Date(values.paidDate).toISOString(),
			...(values.notes.trim() ? { notes: values.notes.trim() } : {}),
		}),
	);

	return (
		<ResponsiveFormDialog
			open
			onOpenChange={(open) => !open && onClose()}
			title={t("financial.payables.payments.add.title")}
			description={t("financial.payables.payments.add.description")}
			pending={isSubmitting}
			footer={
				<>
					<Button
						type="button"
						variant="outline"
						disabled={isSubmitting}
						onClick={onClose}
					>
						{t("common.cancel")}
					</Button>
					<SubmitButton
						form={formId}
						pending={isSubmitting}
						data-testid="add-payment-submit"
					>
						{t("financial.payables.payments.add.confirm")}
					</SubmitButton>
				</>
			}
		>
			<Form {...form}>
				<form id={formId} className="space-y-4 py-2" onSubmit={guardedSubmit}>
					<FormField
						control={form.control}
						name="amount"
						render={({ field }) => (
							<FormItem>
								<FormLabel required>
									{t("financial.payables.payments.add.amount")}
								</FormLabel>
								<FormControl>
									<NumberField
										value={field.value}
										onChange={field.onChange}
										onBlur={field.onBlur}
										name={field.name}
										ref={field.ref}
										suffix="R$"
									/>
								</FormControl>
								<FormMessage />
							</FormItem>
						)}
					/>
					<FormField
						control={form.control}
						name="paidDate"
						render={({ field }) => (
							<FormItem>
								<FormLabel required>
									{t("financial.payables.payments.add.paidDate")}
								</FormLabel>
								<FormControl>
									<Input type="date" {...field} />
								</FormControl>
								<FormMessage />
							</FormItem>
						)}
					/>
					<FormField
						control={form.control}
						name="notes"
						render={({ field }) => (
							<FormItem>
								<FormLabel>
									{t("financial.payables.payments.add.notes")}
								</FormLabel>
								<FormControl>
									<Textarea {...field} rows={2} />
								</FormControl>
								<FormMessage />
							</FormItem>
						)}
					/>
					<FormRootMessage />
				</form>
			</Form>
		</ResponsiveFormDialog>
	);
}
